import { Routes, Route } from "react-router-dom";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useMemo } from "react";
import { managernavigationLinks } from "../data/navData";
import DashboardLayout from "../layout/DashboardLayout/DashboardLayout";
import ProtectedRoute from "./ProtectedRoute";
import Users from "../pages/managerPage/ManagerUser";
import MAsset from "../pages/managerPage/MAsset";
import Category from "../pages/managerPage/AssetCategory";
import BuildingAssets from "../pages/managerPage/BuildingAssets";
import LandscapingAssets from "../pages/managerPage/LandscapingAssets";
import MachineryAssets from "../pages/managerPage/MachineryAssets";
import RoomAssets from "../pages/managerPage/RoomAssets";
import ManagerDashboard from "../pages/managerPage/ManagerHome";
import PMaintenance from "../pages/managerPage/PMaintenance";
import RepairReport from "../pages/managerPage/RepairReport";
import Repair from "../pages/managerPage/Repair";
import MaintenanceReport from "../pages/managerPage/maintenanceReport";
import VendorManagement from "../pages/managerPage/VendorManagement";
import Account from "../components/Account/Account";
import FeedbackReport from "../pages/FeedbackReport2";
import Calendar from "../components/Calendar/Calendar";
import CalendarOverview from "../components/Calendar/CalendarOverview";
import ManagerAttendance from "../pages/managerPage/ManagerAttendance";
import ManagerAttendanceRecords from "../pages/managerPage/ManagerAttendanceRecords";
import ManagerLeaveRecords from "../pages/managerPage/ManagerLeaveRecords";
import ApplyManualLeave from "../pages/managerPage/ApplyManualLeave";
import ManagerApplyLeave from "../pages/managerPage/ManagerApplyLeave";
import ManagerEmployeeLeaveRequests from "../pages/managerPage/ManagerEmployeeLeaveRequests";
import ManagerEmployeeAttendance from "../pages/managerPage/ManagerEmployeeAttendance";
import AttendanceRecordDetail from "../pages/attendance/AttendanceRecordDetail";
import OvertimeDashboard from "../pages/managerPage/OvertimeDashboard";
import OvertimeRequests from "../pages/managerPage/OvertimeRequests";
import UserDetails from "../pages/UserDetails";
import DynamicAssetCategory from "../pages/managerPage/DynamicAssetCategory";
import Room from "../pages/managerPage/AssetCategories/Room";
import {
  useGetUserByEmailQuery,
  useGetDepartmentByIdQuery,
} from "../slices/userApiSlice";

const selectUserInfo = (state) => state.auth.userInfo || {};
const getUserEmail = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.username || "",
);

const ManagerRoutes = () => {
  const email = useSelector(getUserEmail);
  const { data: userData } = useGetUserByEmailQuery(email, {
    skip: !email,
  });

  const userDepartmentId = userData?.user?.departmentId;
  const { data: departmentData } = useGetDepartmentByIdQuery(userDepartmentId, {
    skip: !userDepartmentId,
  });

  const { data: categories } =
    useSelector((state) => state.assetApi?.queries?.getCategory?.data) ||
    useSelector(
      (state) => state.api?.queries?.["getCategory(undefined)"]?.data,
    ) ||
    [];

  // Check if user is from maintenance department (case-insensitive)
  const isMaintenanceManager = useMemo(() => {
    const departmentName = departmentData?.name || "";
    return departmentName?.toLowerCase() === "maintenance";
  }, [departmentData]);

  // Filter navigation links based on department
  const filteredNavigationLinks = useMemo(() => {
    if (isMaintenanceManager) {
      // Maintenance managers see all links
      return managernavigationLinks;
    } else {
      // Non-maintenance managers see: Home, Users, Attendance, Overtime, Feedback, Vendor
      const allowedIds = [1, 2, 9, 12, 11, 10, 3, 4, 5, 6, 7, 8]; // Home, Users, Attendance, Overtime, Feedback, Vendor, Assets
      return managernavigationLinks.filter((link) =>
        allowedIds.includes(link.id),
      );
    }
  }, [isMaintenanceManager]);

  return (
    <DashboardLayout
      navigationLinks={filteredNavigationLinks}
      roleBasePath="manager"
      useApiLogout={true}
    >
      <Routes>
        <Route
          path=""
          element={
            <ProtectedRoute
              element={<ManagerDashboard />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="attendance/record/record/:id"
          element={
            <ProtectedRoute
              element={<AttendanceRecordDetail />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute element={<Users />} requiredRole="Manager" />
          }
        />
        <Route
          path="users/:email"
          element={
            <ProtectedRoute element={<UserDetails />} requiredRole="Manager" />
          }
        />
        <Route
          path="assets"
          element={
            <ProtectedRoute element={<MAsset />} requiredRole="Manager" />
          }
        />
        <Route
          path="assets/building"
          element={
            <ProtectedRoute
              element={<BuildingAssets />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="assets/landscaping"
          element={
            <ProtectedRoute
              element={<LandscapingAssets />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="assets/machinery"
          element={
            <ProtectedRoute
              element={<MachineryAssets />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="assets/room"
          element={
            <ProtectedRoute element={<RoomAssets />} requiredRole="Manager" />
          }
        />
        <Route
          path="assets/:categoryName"
          element={
            <ProtectedRoute
              element={<DynamicAssetCategory />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="category"
          element={
            <ProtectedRoute element={<Category />} requiredRole="Manager" />
          }
        />
        <Route
          path="repair-maintenance"
          element={
            <ProtectedRoute element={<Repair />} requiredRole="Manager" />
          }
        />
        <Route
          path="preventive-maintenance"
          element={
            <ProtectedRoute element={<PMaintenance />} requiredRole="Manager" />
          }
        />
        <Route
          path="repair-report"
          element={
            <ProtectedRoute element={<RepairReport />} requiredRole="Manager" />
          }
        />
        <Route
          path="maintenance-report"
          element={
            <ProtectedRoute
              element={<MaintenanceReport />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="account"
          element={
            <ProtectedRoute element={<Account />} requiredRole="Manager" />
          }
        />
        <Route
          path="feedback"
          element={
            <ProtectedRoute
              element={<FeedbackReport />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="vendor"
          element={
            <ProtectedRoute
              element={<VendorManagement />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="calendar"
          element={
            <ProtectedRoute
              element={<CalendarOverview />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="calendar/:year"
          element={
            <ProtectedRoute element={<Calendar />} requiredRole="Manager" />
          }
        />
        <Route
          path="attendance"
          element={
            <ProtectedRoute
              element={<ManagerAttendance />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="attendance/record"
          element={
            <ProtectedRoute
              element={<ManagerAttendanceRecords />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="attendance/record/:id"
          element={
            <ProtectedRoute
              element={<AttendanceRecordDetail />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="attendance/leave-records"
          element={
            <ProtectedRoute
              element={<ManagerLeaveRecords />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="attendance/apply-leave"
          element={
            <ProtectedRoute
              element={<ManagerApplyLeave />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="attendance/employee-leave-requests"
          element={
            <ProtectedRoute
              element={<ManagerEmployeeLeaveRequests />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="attendance/employee-manual-leave"
          element={
            <ProtectedRoute
              element={<ApplyManualLeave />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="attendance/employee-attendance"
          element={
            <ProtectedRoute
              element={<ManagerEmployeeAttendance />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="overtime"
          element={
            <ProtectedRoute
              element={<OvertimeDashboard />}
              requiredRole="Manager"
            />
          }
        />
        <Route
          path="overtime/requests"
          element={
            <ProtectedRoute
              element={<OvertimeRequests />}
              requiredRole="Manager"
            />
          }
        />
        <Route path="*" element={<h1>404 - Page Not Found</h1>} />
      </Routes>
    </DashboardLayout>
  );
};

export default ManagerRoutes;
